import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CATEGORIES, FUNC_BY_ID, missingStep, buildInputList, toOpts } from '../lib/catalog';
import { fetchInputs, jobImageUrl } from '../lib/api';
import useJob from '../lib/useJob';
import { renderResult } from '../components/Charts';

/** Every analysis with its division attached — CATEGORIES' own funcs are
    bare, and the readout needs to know where each one lives. */
const ALL = CATEGORIES.flatMap((c) => c.funcs.map((f) => ({ ...f, category: c.id })));

const BLANK = {
  year: '', race: '', session: '',
  drivers: [], driver: '', d1: '', d2: '',
  lap: '', lap1: '', lap2: '', dist1: '', dist2: '',
};

/** Analyses whose result is a drawn chart rather than a table or card. */
const CHART_TYPES = new Set([
  'fastest', 'laps', 'time', 'distance', 'delta', 'telemetry',
  'positions', 'strategy', 'racetrace', 'battles', 'cornering',
]);

function Field({ label, hint, wide, children }) {
  return (
    <label className={`field pv-field${wide ? ' pv-field--wide' : ''}`}>
      <span className="pv-flabel">
        {label}
        {hint && <em className="pv-hint"> — {hint}</em>}
      </span>
      {children}
    </label>
  );
}


/** Below 900px the bank shows one division at a time, driven by the strip —
 *  a 23-row list inside a scroll box nested in a page scroll is the single
 *  most annoying thing you can do to a thumb. */
function useNarrow(max = 900) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${max}px)`).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${max}px)`);
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [max]);
  return narrow;
}

/** Is this need already satisfied by what the visitor has chosen? */
function satisfied(need, v) {
  switch (need) {
    case 'year': return Boolean(v.year);
    case 'race': return Boolean(v.race);
    case 'session': return Boolean(v.session);
    case 'lap': case 'optLap': case 'optLap1': case 'optLap2':
      return Boolean(v.session);
    case 'optDriver': case 'd1': case 'd2':
      return Boolean(v.session);
    case 'dist1': case 'dist2':
      return Boolean(v.session);
    case 'drivers': case 'singleDriver':
      return v.drivers.length > 0 || (need === 'drivers' && Boolean(v.session));
    default: return false;
  }
}

export default function Analysis() {
  const [cat, setCat] = useState(CATEGORIES[0].id);   // category at the top of the bank
  const [funcId, setFuncId] = useState('');
  const [values, setValues] = useState(BLANK);
  const [opts, setOpts] = useState({ years: [], races: [], sessions: [], drivers: [], laps: [] });
  const [inputsLoading, setInputsLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const outRef = useRef(null);
  const bankRef = useRef(null);
  const groupRefs = useRef({});

  const func = funcId ? FUNC_BY_ID[funcId] : null;
  const narrow = useNarrow();
  const { status, id, result, error, elapsed, run, reset } = useJob();
  const visibleGroups = narrow ? CATEGORIES.filter((c) => c.id === cat) : CATEGORIES;

  const busy = status === 'queued' || status === 'running';
  const set = (patch) => setValues((v) => ({ ...v, ...patch }));

  /* ── inputs ─────────────────────────────────────────────────────────
     Four independent keys (years / races / sessions / all) feed four
     controls. Each gets its own generation counter rather than one shared
     AbortController: aborting "years" the moment "races" is requested was
     dropping the year list on a fast click, and a slow response landing
     after a category switch repopulated a control the visitor had already
     moved away from. Stale generations are simply ignored. */
  const genRef = useRef({});
  const inflightRef = useRef(new Set());

  const loadInputs = useCallback((input, data) => {
    const gen = (genRef.current[input] || 0) + 1;
    genRef.current[input] = gen;
    inflightRef.current.add(input);
    setInputsLoading(true);
    return fetchInputs(input, data)
      .then((r) => {
        if (genRef.current[input] !== gen) return;
        const res = r.result;
        setOpts((o) => {
          if (input === 'years') return { ...o, years: toOpts(res) };
          if (input === 'races') return { ...o, races: toOpts(res) };
          if (input === 'sessions') return { ...o, sessions: toOpts(res) };
          if (input === 'all') return { ...o, drivers: toOpts(res?.[0]), laps: toOpts(res?.[1]) };
          return o;
        });
      })
      .catch(() => { /* a failed input list just leaves the control empty */ })
      .finally(() => {
        if (genRef.current[input] !== gen) return;
        inflightRef.current.delete(input);
        setInputsLoading(inflightRef.current.size > 0);
      });
  }, []);

  /** Invalidate every in-flight input request — used when the analysis
      itself changes, so nothing from the previous one can land late. */
  const invalidateInputs = useCallback(() => {
    ['years', 'races', 'sessions', 'all'].forEach((k) => {
      genRef.current[k] = (genRef.current[k] || 0) + 1;
      inflightRef.current.delete(k);
    });
    setInputsLoading(false);
  }, []);

  useEffect(() => () => { inflightRef.current.clear(); }, []);

  /* ── fore-edge rail: follow the bank ───────────────────────────────── */
  const scrollToGroup = useCallback((id) => {
    const bank = bankRef.current;
    const el = groupRefs.current[id];
    if (!bank || !el) return;
    // offsetTop is document-relative (the offsetParent is body, not the
    // bank), so scrolling to it landed nowhere near the group. Move by the
    // difference between where the header is and where the bank's top is.
    const delta = el.getBoundingClientRect().top - bank.getBoundingClientRect().top;
    bank.scrollBy({ top: Math.round(delta), behavior: 'smooth' });
  }, []);

  /**
   * Re-open a past run with its context intact. Re-fires the input cascade
   * in dependency order so every select is populated before the visitor
   * touches it — otherwise they land back on an empty form.
   */
  const restore = (entry) => {
    const f = FUNC_BY_ID[entry.id];
    if (!f) return;
    const v = entry.values;
    setFuncId(entry.id);
    setCat(entry.category);
    setValues(v);
    setOpts({ years: [], races: [], sessions: [], drivers: [], laps: [] });
    reset();
    invalidateInputs();
    loadInputs('years', { func: entry.id })
      .then(() => {
        if (v.year && f.needs.includes('race')) {
          return loadInputs('races', { func: entry.id, year: v.year });
        }
        if (v.year && (f.needs.includes('drivers') || f.needs.includes('singleDriver'))) {
          return loadInputs('all', { year: v.year, race: null, session: null });
        }
        return undefined;
      })
      .then(() => {
        if (v.year && v.race && f.needs.includes('session')) {
          return loadInputs('sessions', { year: v.year, race: v.race });
        }
        return undefined;
      })
      .then(() => {
        if (v.year && v.race && v.session && f.needs.includes('session')) {
          return loadInputs('all', { year: v.year, race: v.race, session: v.session });
        }
        return undefined;
      })
      .then(() => scrollToGroup(entry.category));
    requestAnimationFrame(() => scrollToGroup(entry.category));
  };

  const chooseFunc = (id, categoryId) => {
    setFuncId(id);
    setCat(categoryId);
    setValues(BLANK);
    setOpts({ years: [], races: [], sessions: [], drivers: [], laps: [] });
    reset();
    invalidateInputs();
    loadInputs('years', { func: id });
    requestAnimationFrame(() => scrollToGroup(categoryId));
  };

  // Scroll spy: the tab that is "current" is whichever group is at the top
  // of the bank, not whichever was clicked — the rail reports where you are.
  const onBankScroll = useCallback(() => {
    const bank = bankRef.current;
    if (!bank) return;
    // Same trap as scrollToGroup: offsetTop is measured from the document,
    // the scroll position from the bank, so the two never met — only
    // SESSION (56px) and TELEMETRY (225px) could ever become current while
    // the bank scrolled to 459. Compare geometry instead: the group whose
    // header has reached the bank's top edge.
    const bankTop = bank.getBoundingClientRect().top;
    let current = CATEGORIES[0].id;
    for (const c of CATEGORIES) {
      const el = groupRefs.current[c.id];
      if (el && el.getBoundingClientRect().top - bankTop <= 1) current = c.id;
    }
    setCat((prev) => (prev === current ? prev : current));
  }, []);

  /* ── context ───────────────────────────────────────────────────────── */
  const onYear = (year) => {
    if (!year) return;
    set({ year, race: '', session: '', drivers: [], driver: '', d1: '', d2: '', lap: '', lap1: '', lap2: '' });
    setOpts((o) => ({ ...o, races: [], sessions: [], drivers: [], laps: [] }));
    if (func && (func.needs.includes('drivers') || func.needs.includes('singleDriver'))
        && !func.needs.includes('race')) {
      loadInputs('all', { year, race: null, session: null });
    } else if (func && func.needs.includes('race')) {
      loadInputs('races', { func: funcId, year });
    }
  };

  const onRace = (race) => {
    if (!race) return;
    set({ race, session: '', drivers: [], driver: '', d1: '', d2: '', lap: '', lap1: '', lap2: '' });
    setOpts((o) => ({ ...o, sessions: [], drivers: [], laps: [] }));
    if (!func) return;
    if (funcId === 'racetrace') loadInputs('all', { year: values.year, race, session: 'Race' });
    else if (func.needs.includes('session')) loadInputs('sessions', { year: values.year, race });
  };

  const onSession = (session) => {
    if (!session) return;
    set({ session, drivers: [], driver: '', d1: '', d2: '', lap: '', lap1: '', lap2: '' });
    if (func) loadInputs('all', { year: values.year, race: values.race, session });
  };

  /* ── run ───────────────────────────────────────────────────────────── */
  const why = missingStep(func, values);
  const canRun = Boolean(func) && !why && !busy;

  const start = () => {
    if (!canRun) return;
    setRecent((r) => [
      { id: funcId, category: func.category, values, at: Date.now() },
      ...r.filter((x) => !(x.id === funcId && JSON.stringify(x.values) === JSON.stringify(values))),
    ].slice(0, 5));
    run(funcId, buildInputList(funcId, values));
    setTimeout(() => outRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' }), 60);
  };

  const needs = useMemo(() => func?.needs || [], [func]);
  const needsSession = needs.includes('session');
  const multiDriver = needs.includes('drivers') || needs.includes('singleDriver');

  const singleSelectDriver = needs.includes('singleDriver');
  const toggleDriver = (code) => {
    if (singleSelectDriver) {
      set({ drivers: values.drivers[0] === code ? [] : [code] });
      return;
    }
    set({
      drivers: values.drivers.includes(code)
        ? values.drivers.filter((d) => d !== code)
        : [...values.drivers, code],
    });
  };

  /* Depth on demand: every other analysis this context already answers. */
  const siblings = useMemo(() => {
    if (!func) return [];
    const ready = (f) => f.id !== funcId && f.needs.every((n) => satisfied(n, values));
    const same = ALL.filter((f) => f.category === func.category && ready(f));
    const rest = ALL.filter((f) => f.category !== func.category && ready(f));
    return [...same, ...rest].slice(0, 6);
  }, [func, funcId, values]);

  const activeCategory = CATEGORIES.find((c) => c.id === (func?.category || cat)) || CATEGORIES[0];

  /* ── output ────────────────────────────────────────────────────────── */
  const inputSummary = useMemo(() => {
    const bits = [];
    if (values.year) bits.push(values.year);
    if (values.race) {
      const race = opts.races.find((o) => o.v === values.race);
      bits.push(race?.l || values.race);   // name · venue
    }
    if (values.session) bits.push(values.session);
    if (values.drivers.length) bits.push(values.drivers.join(' · '));
    if (values.d1 && values.d2) bits.push(`${values.d1} v ${values.d2}`);
    return bits.join('  /  ');
  }, [values, opts.races]);

  const renderOutput = () => {
    if (busy) {
      return (
        <div className="pv-progress">
          <div className="pv-progress-row">
            <span className="pv-progress-label">
              {status === 'queued' ? 'Queued' : 'Analysing'}
            </span>
            <span className="pv-progress-time">{elapsed.toFixed(1)}s</span>
          </div>
          <div className="bar bar--scan"><span className="fill" style={{ width: '100%' }} /></div>
          <p className="pv-progress-note">
            Loading timing data for {inputSummary || 'that session'}.
            A session nobody has asked for before takes about a minute; anything
            already analysed comes back in under a second.
          </p>
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className="alert alert--err" role="alert">
          <b>Failed</b>
          <span>{error}</span>
        </div>
      );
    }
    if (status === 'done' && result) {
      const caption = `${func?.name || 'Chart'} — ${inputSummary}`;
      const isChart = CHART_TYPES.has(result.type);
      const raw = result.image && id
        ? <img src={jobImageUrl(id)} alt={caption}
            style={{ width: '100%', height: 'auto', display: 'block' }} />
        : renderResult(result);
      // A chart is a picture: name it, or a screen reader hears nothing at
      // all. Tables keep their own semantics, so they are never wrapped.
      const body = isChart
        ? <div role="img" aria-label={caption}>{raw}</div>
        : raw;
      return (
        <>
          <div className="pv-plate">
            <span className="pv-plate-tr" aria-hidden="true" />
            <span className="pv-plate-bl" aria-hidden="true" />
            {body}
          </div>
          <div className="pv-caption">
            <span className="pv-caption-title">{func?.name || ''}</span>
            <span>{inputSummary}</span>
            <span className="pv-caption-right">{elapsed.toFixed(1)}s</span>
          </div>
        </>
      );
    }
    return (
      <div className="pv-stage-block">
        <b className="pv-stage-title">Nothing here yet</b>
        <p className="pv-stage-note">
          {narrow ? 'Choose an analysis above, set the year, the grand prix and the session, then run it.'
            : 'Choose an analysis on the left, set the year, the grand prix and the session, then run it.'} The result renders here.
        </p>
      </div>
    );
  };

  /* ── bezel fields ──────────────────────────────────────────────────── */
  // The four input lists arrive independently. Every session-dependent
  // control has to be able to say "still fetching" — reporting emptiness
  // while a request is in flight reads as "this race has no laps", which
  // is a lie the visitor has no way to check.
  const yearsPending = inputsLoading && opts.years.length === 0;
  const racesPending = inputsLoading && opts.races.length === 0 && Boolean(values.year);
  const sessionsPending = inputsLoading && opts.sessions.length === 0 && Boolean(values.race);
  const driversPending = inputsLoading && opts.drivers.length === 0;
  const lapsPending = inputsLoading && opts.laps.length === 0;

  const renderFields = () => (
    <>
      {needs.includes('year') && (
        <Field label="Year">
          <select className="select" value={values.year} onChange={(e) => onYear(e.target.value)}
            disabled={yearsPending} data-pending={yearsPending || undefined}>
            <option value="">{inputsLoading && !opts.years.length ? 'Loading…' : 'Select a year'}</option>
            {opts.years.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('race') && (
        <Field label="Grand prix" hint={values.year ? undefined : 'year first'}>
          <select className="select" value={values.race} onChange={(e) => onRace(e.target.value)}
            disabled={!values.year || racesPending} data-pending={racesPending || undefined}>
            <option value="">{
              !values.year ? '—'
              : inputsLoading && !opts.races.length ? 'Loading…'
              : opts.races.length ? 'Select' : 'No data that year'
            }</option>
            {opts.races.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
      )}

      {needsSession && (
        <Field label="Session" hint={values.race ? undefined : 'grand prix first'}>
          <select className="select" value={values.session} onChange={(e) => onSession(e.target.value)}
            disabled={!values.race || sessionsPending} data-pending={sessionsPending || undefined}>
            <option value="">{
              !values.race ? '—'
              : inputsLoading && !opts.sessions.length ? 'Loading…'
              : opts.sessions.length ? 'Select' : 'No sessions'
            }</option>
            {opts.sessions.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('optDriver') && (
        <Field label="Driver" hint="fastest if skipped">
          <select className="select" value={values.driver}
            onChange={(e) => set({ driver: e.target.value })}
            disabled={!values.session || driversPending} data-pending={driversPending || undefined}>
            <option value="">{!values.session ? '—' : driversPending ? 'Loading…' : 'Fastest driver'}</option>
            {opts.drivers.map((o) => <option key={o.v} value={o.v}>{o.v}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('d1') && (
        <Field label="Driver 1" hint={!values.session ? 'session first' : 'fastest driver if skipped'}>
          <select className="select" value={values.d1} onChange={(e) => set({ d1: e.target.value })}
            disabled={!values.session || driversPending} data-pending={driversPending || undefined}>
            <option value="">{!values.session ? '—' : driversPending ? 'Loading…' : opts.drivers.length ? 'Select' : 'No drivers'}</option>
            {opts.drivers.map((o) => <option key={o.v} value={o.v}>{o.v}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('d2') && (
        <Field label="Driver 2" hint={!values.session ? 'session first' : 'fastest driver if skipped'}>
          <select className="select" value={values.d2} onChange={(e) => set({ d2: e.target.value })}
            disabled={!values.session || driversPending} data-pending={driversPending || undefined}>
            <option value="">{!values.session ? '—' : driversPending ? 'Loading…' : opts.drivers.length ? 'Select' : 'No drivers'}</option>
            {opts.drivers.map((o) => <option key={o.v} value={o.v}>{o.v}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('lap') && (
        <Field label="Lap" hint={values.session ? undefined : 'session first'}>
          <select className="select" value={values.lap}
            onChange={(e) => set({ lap: e.target.value ? Number(e.target.value) : '' })}
            disabled={!values.session || lapsPending} data-pending={lapsPending || undefined}>
            <option value="">{!values.session ? '—' : lapsPending ? 'Loading…' : opts.laps.length ? 'Select a lap' : 'No laps recorded'}</option>
            {opts.laps.map((o) => <option key={o.v} value={o.v}>Lap {o.l}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('optLap') && (
        <Field label="Lap" hint="fastest if skipped">
          <select className="select" value={values.lap}
            onChange={(e) => set({ lap: e.target.value ? Number(e.target.value) : '' })}
            disabled={!values.session || lapsPending} data-pending={lapsPending || undefined}>
            <option value="">{!values.session ? '—' : lapsPending ? 'Loading…' : 'Fastest lap'}</option>
            {opts.laps.map((o) => <option key={o.v} value={o.v}>Lap {o.l}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('optLap1') && (
        <Field label="Lap 1" hint="fastest if skipped">
          <select className="select" value={values.lap1}
            onChange={(e) => set({ lap1: e.target.value ? Number(e.target.value) : '' })}
            disabled={!values.session || lapsPending} data-pending={lapsPending || undefined}>
            <option value="">{!values.session ? '—' : lapsPending ? 'Loading…' : 'Fastest lap'}</option>
            {opts.laps.map((o) => <option key={o.v} value={o.v}>Lap {o.l}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('optLap2') && (
        <Field label="Lap 2" hint="fastest if skipped">
          <select className="select" value={values.lap2}
            onChange={(e) => set({ lap2: e.target.value ? Number(e.target.value) : '' })}
            disabled={!values.session || lapsPending} data-pending={lapsPending || undefined}>
            <option value="">{!values.session ? '—' : lapsPending ? 'Loading…' : 'Fastest lap'}</option>
            {opts.laps.map((o) => <option key={o.v} value={o.v}>Lap {o.l}</option>)}
          </select>
        </Field>
      )}

      {needs.includes('dist1') && (
        <Field label="Start distance (m)" hint="0 for a full lap">
          <input className="input" type="number" min="0" step="10" inputMode="numeric"
            placeholder="Full lap" value={values.dist1}
            onChange={(e) => set({ dist1: e.target.value })} />
        </Field>
      )}

      {needs.includes('dist2') && (
        <Field label="End distance (m)" hint="blank for a full lap">
          <input className="input" type="number" min="0" step="10" inputMode="numeric"
            placeholder="Full lap" value={values.dist2}
            onChange={(e) => set({ dist2: e.target.value })} />
        </Field>
      )}

      {multiDriver && (
        <Field wide
          label={singleSelectDriver ? 'Driver' : 'Drivers'}
          hint={values.session && opts.drivers.length ? 'tap to select' : undefined}>
          {/* A lone dash in a 44px box read as a rendering fault. Say what
              is actually blocking the control, in the control's own shape. */}
          {!values.session && needsSession ? (
            <div className="pv-placeholder" role="status">Pick a session first</div>
          ) : opts.drivers.length ? (
            <div className="pv-drivers" role="group" aria-label="Drivers">
              {opts.drivers.map((o) => (
                <button key={o.v} type="button" className="pv-drv"
                  aria-pressed={values.drivers.includes(o.v)}
                  onClick={() => toggleDriver(o.v)}>
                  {o.v}
                </button>
              ))}
            </div>
          ) : (
            <div className="pv-placeholder" role="status">
              {driversPending ? 'Loading drivers…' : 'No drivers for that session'}
            </div>
          )}
        </Field>
      )}
    </>
  );

  return (
    <div className="pv-chassis">
      {/* ── fore-edge rail + switch bank ── */}
      <div className="pv-col pv-bankcol">
        <nav className="pv-fore" aria-label="Analysis categories">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              className="pv-fore-tab"
              style={{ '--extent': c.funcs.length }}
              aria-current={cat === c.id}
              onClick={() => { setCat(c.id); scrollToGroup(c.id); }}
            >
              <span>
                {c.label}
                <span className="pv-fore-count">{c.funcs.length}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="pv-col-scroll pv-bank" ref={bankRef}
          onScroll={narrow ? undefined : onBankScroll}>
          {visibleGroups.map((c) => (
            <div key={c.id} className="pv-group" role="group" aria-labelledby={`g-${c.id}`}
              ref={(el) => { groupRefs.current[c.id] = el; }}>
              <div className="pv-group-label" id={`g-${c.id}`}>{c.label}</div>
              {c.funcs.map((f) => (
                <button key={f.id} type="button" className="pv-func"
                  aria-pressed={funcId === f.id}
                  onClick={() => chooseFunc(f.id, c.id)}>
                  <span className="pv-func-mark" aria-hidden="true" />
                  {f.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ── binnacle ── */}
      <div className="pv-col pv-binnacle">
        <div className="pv-bezel">
          <div className="pv-bezel-head">
            <div className="pv-bezel-title">
              <h1>{func ? func.name : 'F1 analysis'}</h1>
              <p className="pv-bezel-blurb">
                {func ? func.blurb : `Pick a switch ${narrow ? 'above' : 'on the left'} and this faceplate sets itself.`}
              </p>
            </div>

            {func && (
              <div className="pv-bezel-run">
                <button type="button"
                  className={`btn${canRun ? '' : ' btn--disabled'}`}
                  onClick={start} disabled={!canRun}>
                  {busy ? `Analysing · ${elapsed.toFixed(0)}s` : 'Run analysis'}
                </button>
                {!canRun && <div className="pv-why">{why || 'Working…'}</div>}
              </div>
            )}
          </div>

          {!func && (
            <div className="pv-bezel-fields">
              <div className="pv-hint" style={{ minHeight: 44, width: '100%', display: 'flex', alignItems: 'center' }}>
                No analysis selected — {narrow ? 'flip a switch above' : 'flip a switch on the left'} and this faceplate fills itself in
              </div>
            </div>
          )}

          {func && <div className="pv-bezel-fields">{renderFields()}</div>}
        </div>

        <div className="pv-stage" ref={outRef}>
          {renderOutput()}
        </div>
      </div>

      {/* ── readout strip ── */}
      <aside className="pv-col pv-readout" aria-label="Readout">
        <section className="pv-readout-sec">
          <h2>Selection</h2>
          <div className="pv-fieldline">
            <span className="pv-fieldline-k">Analysis</span>
            <span className="pv-fieldline-lead" />
            <span className="pv-fieldline-v">{func ? func.name : '—'}</span>
          </div>
          <div className="pv-fieldline">
            <span className="pv-fieldline-k">Context</span>
            <span className="pv-fieldline-lead" />
            <span className="pv-fieldline-v">{inputSummary || '—'}</span>
          </div>
          <div className="pv-fieldline">
            <span className="pv-fieldline-k">Division</span>
            <span className="pv-fieldline-lead" />
            <span className="pv-fieldline-v">{activeCategory.label}</span>
          </div>
          {func && <p className="pv-bezel-blurb pv-hide-sm" style={{ marginTop: 8 }}>{func.blurb}</p>}
        </section>

        <section className="pv-readout-sec">
          <h2>State</h2>
          <div className="pv-state-row">
            <b>Status</b>
            <span className={busy ? 'is-live' : ''}>
              {busy ? (status === 'queued' ? 'Queued' : 'Analysing')
                : status === 'done' ? 'Ready'
                : status === 'error' ? 'Failed' : 'Idle'}
            </span>
          </div>
          <div className="pv-state-row">
            <b>Elapsed</b>
            <span>{status === 'idle' ? '—' : `${elapsed.toFixed(1)}s`}</span>
          </div>
          <div className="pv-state-row">
            <b>Result</b>
            <span>{status === 'done' ? 'Cached on the server' : status === 'error' ? error?.slice(0, 40) : '—'}</span>
          </div>
          <div className="pv-state-row">
            <b>Controls</b>
            <span>{inputsLoading ? 'Loading lists…' : 'Ready'}</span>
          </div>
        </section>

        {!func && recent.length === 0 && (
          <section className="pv-readout-sec pv-hide-sm">
            <h2>Start here</h2>
            <div className="pv-siblings">
              {['fastest', 'results', 'strategy', 'positions'].map((id) => {
                const f = ALL.find((x) => x.id === id);
                return f ? (
                  <button key={id} type="button" className="pv-sibling"
                    onClick={() => chooseFunc(f.id, f.category)}>
                    {f.name}
                    <span className="pv-func-mark" aria-hidden="true" />
                  </button>
                ) : null;
              })}
            </div>
            <p className="pv-bezel-blurb" style={{ marginTop: 10 }}>
              Pick any of the {ALL.length} analyses {narrow ? 'above' : 'on the left'} — this strip
              then reports what you chose, what is running, and what else the
              same context can answer.
            </p>
          </section>
        )}

        {recent.length > 0 && (
          <section className="pv-readout-sec">
            <h2>Recent runs</h2>
            <div className="pv-siblings">
              {recent.map((r) => (
                <button key={`${r.id}-${r.at}`} type="button" className="pv-sibling"
                  onClick={() => restore(r)}>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {FUNC_BY_ID[r.id]?.name}
                  </span>
                  <span className="pv-func-mark" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        )}

        {siblings.length > 0 && (
          <section className="pv-readout-sec">
            <h2>Answers this context already supports</h2>
            <div className="pv-siblings">
              {siblings.map((f) => (
                <button key={f.id} type="button" className="pv-sibling"
                  onClick={() => chooseFunc(f.id, f.category)}>
                  {f.name}
                  <span className="pv-func-mark" aria-hidden="true" />
                </button>
              ))}
            </div>
          </section>
        )}
      </aside>
    </div>
  );
}
